import React, { useMemo, useState } from "react";
import { Button, DatePicker, Tag, message } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { errorText, openBlob } from "../api";

// Shared pieces of the receivables report tabs: the period picker, labels for document types,
// statement row kinds, correction reasons and payment-behaviour flags (all translated here;
// the PDFs stay German), and a download button.

export const useRange = (initial = [dayjs().startOf("year"), dayjs()]) => {
  const [range, setRange] = useState(initial);
  const params = useMemo(
    () => ({ from: range[0].format("YYYY-MM-DD"), to: range[1].format("YYYY-MM-DD") }),
    [range]
  );
  const picker = (
    <DatePicker.RangePicker
      value={range}
      allowClear={false}
      format="DD.MM.YYYY"
      onChange={v => v && v[0] && v[1] && setRange([v[0], v[1]])}
    />
  );
  return { range, params, picker };
};

export const useLabels = () => {
  const { t } = useTranslation("accounting");
  const type = {
    invoice: t("Invoice"),
    partial: t("Partial invoice"),
    advance: t("Advance invoice"),
    final: t("Final invoice"),
    credit_note: t("Invoice correction"),
    cancellation: t("Cancellation invoice"),
    correction_notice: t("Correction notice")
  };
  const kind = {
    invoice: t("Invoice"),
    correction: t("Invoice correction"),
    cancellation: t("Cancellation invoice"),
    payment: t("Payment"),
    offset: t("Offset against credit"),
    refund: t("Refund"),
    payment_reversal: t("Returned payment"),
    return_fee: t("Return debit fee"),
    dunning_fee: t("Dunning fees and interest"),
    dunning_waiver: t("Dunning charges waived"),
    write_off: t("Bad debt write-off"),
    write_off_revival: t("Payment on a written-off receivable"),
    reversal: t("Reversal"),
    opening: t("Balance carried forward"),
    other: t("Other booking"),
    advance_on_payment: t("Advance invoice (booked on payment)"),
    correction_notice: t("Correction notice")
  };
  const reason = {
    defect: t("Defects / price reduction"),
    return: t("Return"),
    goodwill: t("Goodwill"),
    price_error: t("Price error"),
    quantity_error: t("Quantity error"),
    bonus: t("Bonus / discount"),
    other: t("Other"),
    "": t("No reason given")
  };
  const flag = {
    prepayment_suggested: { color: "red", text: t("Prepayment suggested") },
    slow_payer: { color: "orange", text: t("Slow payer") },
    dunning_block_suggested: { color: "purple", text: t("Dunning block suggested") },
    reliable: { color: "green", text: t("Reliable payer") }
  };
  const flagTags = flags =>
    (flags || []).map(f => (
      <Tag key={f} color={flag[f]?.color}>
        {flag[f]?.text || f}
      </Tag>
    ));
  return { type, kind, reason, flag, flagTags };
};

export const DownloadButton = ({ label, promise, filename, type }) => {
  const [busy, setBusy] = useState(false);
  return (
    <Button
      type={type}
      loading={busy}
      onClick={async () => {
        setBusy(true);
        try {
          await openBlob(promise(), filename);
        } catch (error) {
          message.error(errorText(error));
        } finally {
          setBusy(false);
        }
      }}
    >
      {label}
    </Button>
  );
};
